/**
 * Reminder Settings Page - Terminerinnerungen konfigurieren
 * 
 * Ermöglicht die Einstellung von zwei Erinnerungen mit:
 * - Zeitabständen zum Termin
 * - Anpassbaren E-Mail-Texten
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import {
  Bell,
  Clock,
  Mail,
  Save,
  RotateCcw,
  Info,
  Loader2,
  CheckCircle,
  Copy,
  ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Format interval for display
const formatInterval = (interval) => {
  if (!interval) return "";
  const { value, unit } = interval;
  if (unit === "hours") {
    return value === 1 ? "1 Stunde" : `${value} Stunden`;
  }
  if (unit === "days") {
    return value === 1 ? "1 Tag" : `${value} Tage`;
  }
  return `${value} ${unit}`;
};

// Get interval key for select
const getIntervalKey = (interval) => {
  if (!interval) return "1-days";
  return `${interval.value}-${interval.unit}`;
};

// Parse interval key
const parseIntervalKey = (key) => {
  const [value, unit] = key.split("-");
  return { value: parseInt(value), unit };
};

export default function ReminderSettingsPage() {
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [options, setOptions] = useState({ intervals: [], variables: [] });
  const [hasChanges, setHasChanges] = useState(false);
  
  // Form state
  const [reminder1, setReminder1] = useState({
    enabled: true,
    interval: { value: 1, unit: "days" },
    subject: "",
    body_template: ""
  });
  
  const [reminder2, setReminder2] = useState({
    enabled: false,
    interval: { value: 3, unit: "hours" },
    subject: "",
    body_template: ""
  });

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const [settingsRes, optionsRes] = await Promise.all([
        fetch(`${API_URL}/api/reminder-settings/`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/reminder-settings/options`, {
          headers: { "Authorization": `Bearer ${token}` }
        })
      ]);
      
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
        setReminder1(data.reminder_1 || reminder1);
        setReminder2(data.reminder_2 || reminder2);
      }
      
      if (optionsRes.ok) {
        const data = await optionsRes.json();
        setOptions(data);
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/reminder-settings/`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          reminder_1: reminder1,
          reminder_2: reminder2
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler beim Speichern");
      }
      
      const data = await response.json();
      setSettings(data);
      setHasChanges(false);
      toast.success("Einstellungen gespeichert");
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error(error.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // Handle reset
  const handleReset = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reminder-settings/reset`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Fehler beim Zurücksetzen");
      
      const data = await response.json();
      setSettings(data.settings);
      setReminder1(data.settings.reminder_1);
      setReminder2(data.settings.reminder_2);
      setHasChanges(false);
      toast.success("Einstellungen auf Standardwerte zurückgesetzt");
    } catch (error) {
      console.error("Failed to reset:", error);
      toast.error("Fehler beim Zurücksetzen");
    }
  };

  // Copy variable to clipboard
  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.success(`"${variable}" kopiert`);
  };

  // Update reminder and mark changes
  const updateReminder1 = (updates) => {
    setReminder1(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const updateReminder2 = (updates) => {
    setReminder2(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/termine">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="h-6 w-6" />
              Terminerinnerungen
            </h1>
            <p className="text-muted-foreground">
              Konfigurieren Sie automatische E-Mail-Erinnerungen für Ihre Kunden
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              Ungespeicherte Änderungen
            </Badge>
          )}
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <RotateCcw className="h-4 w-4 mr-2" />
                Zurücksetzen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Auf Standardwerte zurücksetzen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Alle Ihre Anpassungen werden gelöscht und die Standardtexte wiederhergestellt.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={handleReset}>Zurücksetzen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Reminder 1 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>Erinnerung 1</CardTitle>
                    <CardDescription>Haupterinnerung vor dem Termin</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="reminder1-enabled" className="text-sm">Aktiv</Label>
                  <Switch
                    id="reminder1-enabled"
                    checked={reminder1.enabled}
                    onCheckedChange={(checked) => updateReminder1({ enabled: checked })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className={`space-y-4 ${!reminder1.enabled ? "opacity-50 pointer-events-none" : ""}`}>
              {/* Interval Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Zeitpunkt
                </Label>
                <Select
                  value={getIntervalKey(reminder1.interval)}
                  onValueChange={(key) => updateReminder1({ interval: parseIntervalKey(key) })}
                >
                  <SelectTrigger data-testid="reminder1-interval">
                    <SelectValue placeholder="Zeitabstand wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.intervals.map((opt) => (
                      <SelectItem key={`${opt.value}-${opt.unit}`} value={`${opt.value}-${opt.unit}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Subject */}
              <div className="space-y-2">
                <Label>Betreffzeile</Label>
                <Input
                  value={reminder1.subject}
                  onChange={(e) => updateReminder1({ subject: e.target.value })}
                  placeholder="Terminerinnerung für {appointment_date}"
                  data-testid="reminder1-subject"
                />
              </div>
              
              {/* Body */}
              <div className="space-y-2">
                <Label>E-Mail-Text</Label>
                <Textarea
                  value={reminder1.body_template}
                  onChange={(e) => updateReminder1({ body_template: e.target.value })}
                  placeholder="Guten Tag {customer_name},..."
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="reminder1-body"
                />
              </div>
            </CardContent>
          </Card>

          {/* Reminder 2 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Mail className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle>Erinnerung 2</CardTitle>
                    <CardDescription>Zusätzliche Erinnerung (z.B. am Tag des Termins)</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="reminder2-enabled" className="text-sm">Aktiv</Label>
                  <Switch
                    id="reminder2-enabled"
                    checked={reminder2.enabled}
                    onCheckedChange={(checked) => updateReminder2({ enabled: checked })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className={`space-y-4 ${!reminder2.enabled ? "opacity-50 pointer-events-none" : ""}`}>
              {/* Interval Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Zeitpunkt
                </Label>
                <Select
                  value={getIntervalKey(reminder2.interval)}
                  onValueChange={(key) => updateReminder2({ interval: parseIntervalKey(key) })}
                >
                  <SelectTrigger data-testid="reminder2-interval">
                    <SelectValue placeholder="Zeitabstand wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.intervals.map((opt) => (
                      <SelectItem key={`${opt.value}-${opt.unit}`} value={`${opt.value}-${opt.unit}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Subject */}
              <div className="space-y-2">
                <Label>Betreffzeile</Label>
                <Input
                  value={reminder2.subject}
                  onChange={(e) => updateReminder2({ subject: e.target.value })}
                  placeholder="Termin heute um {appointment_time} Uhr"
                  data-testid="reminder2-subject"
                />
              </div>
              
              {/* Body */}
              <div className="space-y-2">
                <Label>E-Mail-Text</Label>
                <Textarea
                  value={reminder2.body_template}
                  onChange={(e) => updateReminder2({ body_template: e.target.value })}
                  placeholder="Guten Tag {customer_name},..."
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="reminder2-body"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Variables Help */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="h-5 w-5" />
                Platzhalter
              </CardTitle>
              <CardDescription>
                Diese Platzhalter werden automatisch durch die entsprechenden Werte ersetzt
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {options.variables.map((v) => (
                  <TooltipProvider key={v.variable}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className="flex items-center justify-between p-2 bg-muted/50 rounded cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => copyVariable(v.variable)}
                        >
                          <code className="text-sm font-mono text-blue-600">{v.variable}</code>
                          <Copy className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>{v.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">Klicken zum Kopieren</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-2">So funktionieren die Erinnerungen</p>
                  <ul className="space-y-1 text-blue-700">
                    <li>• Erinnerungen werden automatisch zum eingestellten Zeitpunkt gesendet</li>
                    <li>• Der Versand erfolgt nur für Termine mit Status "Geplant" oder "Bestätigt"</li>
                    <li>• Abgesagte oder vergangene Termine erhalten keine Erinnerung</li>
                    <li>• Sie können Erinnerungen auch manuell in der Terminübersicht senden</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Zusammenfassung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Erinnerung 1</span>
                <Badge variant={reminder1.enabled ? "default" : "secondary"}>
                  {reminder1.enabled ? `${formatInterval(reminder1.interval)} vorher` : "Deaktiviert"}
                </Badge>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm">Erinnerung 2</span>
                <Badge variant={reminder2.enabled ? "default" : "secondary"}>
                  {reminder2.enabled ? `${formatInterval(reminder2.interval)} vorher` : "Deaktiviert"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
